import React, { useState } from "react";
import { Send, CheckCircle2, MapPin, Mail, Phone, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const needs = [
  "Audit d'IA — Évaluer mon potentiel",
  "Box IA — Serveur local",
  "Logiciel IA Sur Mesure",
  "IA Sentinelle — Cybersécurité",
  "Formation & Conformité Loi 25",
  "Autre demande",
];

const inputStyle = {
  width: "100%",
  border: "1px solid rgba(0,0,0,0.10)",
  borderRadius: "12px",
  padding: "13px 16px",
  fontSize: "0.9375rem",
  color: "#1d1d1f",
  fontFamily: "'Inter', sans-serif",
  background: "#fafafa",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

export default function ContactForm() {
  const [form, setForm] = useState({ nom: "", compagnie: "", courriel: "", telephone: "", besoin: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error,  setError]  = useState("");
  const [focused, setFocused] = useState(null);

  const onChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      setForm({ nom: "", compagnie: "", courriel: "", telephone: "", besoin: "", message: "" });
    } catch {
      setStatus("error");
      setError("Une erreur est survenue. Réessayez ou appelez-nous directement.");
    }
  };

  const getFocusStyle = (name) =>
    focused === name
      ? { ...inputStyle, borderColor: "#0071e3", boxShadow: "0 0 0 3px rgba(0,113,227,0.15)", background: "#fff" }
      : inputStyle;

  return (
    <section id="contact" style={{ background: "#ffffff" }} className="section-pad">
      <div className="section-wrap">

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "56px" }}>
          <span className="eyebrow">Contact</span>
          <h2 className="h-section">
            Parlons de votre
            <br />
            <span className="text-gradient">projet ensemble.</span>
          </h2>
          <p className="body-lg" style={{ maxWidth: "420px", margin: "16px auto 0" }}>
            Remplissez le formulaire — on vous répond dans les 24h, sans engagement.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.8fr", gap: "24px", alignItems: "start" }}
             className="contact-grid">

          {/* ── Left info ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

            <div className="card-apple" style={{ padding: "28px" }}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: "1rem", fontWeight: 700, color: "#1d1d1f", marginBottom: "20px" }}>
                Coordonnées
              </h3>
              {[
                { icon: <MapPin size={14}/>,  label: "Siège social",  val: "Québec, Canada"         },
                { icon: <Mail size={14}/>,    label: "Courriel",      val: "info@ainnovation.ca"   },
                { icon: <Phone size={14}/>,   label: "Téléphone",     val: "+1 (450) 735-1466"      },
                { icon: <Clock size={14}/>,   label: "Disponibilité", val: "Lun–Ven, 8h–18h"       },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: i < 3 ? "14px" : 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "10px", background: "rgba(0,113,227,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#0071e3", flexShrink: 0 }}>
                    {r.icon}
                  </div>
                  <div>
                    <p style={{ fontSize: "0.75rem", color: "#6e6e73" }}>{r.label}</p>
                    <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1d1d1f" }}>{r.val}</p>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                background: "linear-gradient(145deg, #0071e3 0%, #0a84ff 100%)",
                borderRadius: "20px",
                padding: "28px",
              }}
            >
              <p style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: "1rem", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>
                Appel gratuit de 30 min
              </p>
              <p style={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>
                On vous explique concrètement ce qu'on peut faire pour vous — sans engagement, sans jargon.
              </p>
            </div>
          </div>

          {/* ── Form ── */}
          <div className="card-apple" style={{ padding: "40px" }}>
            {status === "success" ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "48px 0" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(52,199,89,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                  <CheckCircle2 size={30} style={{ color: "#34c759" }} />
                </div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: "1.25rem", fontWeight: 700, color: "#1d1d1f" }}>Message envoyé!</h3>
                <p style={{ marginTop: "10px", fontSize: "0.9375rem", color: "#6e6e73", maxWidth: "280px" }}>
                  Un expert vous contactera dans les 24 heures ouvrables.
                </p>
                <button onClick={() => setStatus("idle")} className="btn-primary" style={{ marginTop: "28px" }}>
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}
                     className="form-grid-mobile">
                  <Field label="Votre nom" name="nom" value={form.nom} onChange={onChange}
                         placeholder="Jean Tremblay" focused={focused} setFocused={setFocused} getFocusStyle={getFocusStyle} />
                  <Field label="Compagnie" name="compagnie" value={form.compagnie} onChange={onChange}
                         placeholder="Acme Solutions inc." focused={focused} setFocused={setFocused} getFocusStyle={getFocusStyle} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}
                     className="form-grid-mobile">
                  <Field label="Courriel" name="courriel" type="email" value={form.courriel} onChange={onChange}
                         placeholder="jean@compagnie.ca" focused={focused} setFocused={setFocused} getFocusStyle={getFocusStyle} />
                  <Field label="Téléphone" name="telephone" type="tel" value={form.telephone} onChange={onChange}
                         placeholder="+1 (514) 000-0000" required={false} focused={focused} setFocused={setFocused} getFocusStyle={getFocusStyle} />
                </div>

                <div>
                  <Label>Besoin principal</Label>
                  <select name="besoin" value={form.besoin} onChange={onChange} required
                    style={getFocusStyle("besoin")}
                    onFocus={() => setFocused("besoin")} onBlur={() => setFocused(null)}>
                    <option value="">Choisissez une option...</option>
                    {needs.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div>
                  <Label>Décrivez votre situation</Label>
                  <textarea name="message" value={form.message} onChange={onChange} required rows={5}
                    placeholder="Ex : Nous avons 25 employés, nous traitons beaucoup de documents papier manuellement..."
                    style={{ ...getFocusStyle("message"), resize: "none" }}
                    onFocus={() => setFocused("message")} onBlur={() => setFocused(null)} />
                </div>

                {status === "error" && (
                  <p style={{ background: "rgba(255,59,48,0.07)", border: "1px solid rgba(255,59,48,0.2)", borderRadius: "10px", padding: "12px 16px", fontSize: "0.875rem", color: "#ff3b30" }}>
                    {error}
                  </p>
                )}

                <button type="submit" disabled={status === "loading"} className="btn-primary"
                        style={{ justifyContent: "center", padding: "16px", fontSize: "1rem", opacity: status === "loading" ? 0.6 : 1, cursor: status === "loading" ? "not-allowed" : "pointer" }}>
                  {status === "loading"
                    ? <><div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Envoi...</>
                    : <>Envoyer ma demande <Send size={16} /></>}
                </button>

                <p style={{ textAlign: "center", fontSize: "0.8125rem", color: "#6e6e73" }}>
                  Vos informations sont confidentielles et ne seront jamais partagées.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; }
          .form-grid-mobile { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function Label({ children }) {
  return (
    <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#1d1d1f", marginBottom: "7px", fontFamily: "'Plus Jakarta Sans'" }}>
      {children} <span style={{ color: "#0071e3" }}>*</span>
    </label>
  );
}

function Field({ label, name, type = "text", value, onChange, placeholder, required = true, focused, setFocused, getFocusStyle }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#1d1d1f", marginBottom: "7px", fontFamily: "'Plus Jakarta Sans'" }}>
        {label} {required ? <span style={{ color: "#0071e3" }}>*</span> : <span style={{ color: "#6e6e73", fontWeight: 400 }}>(optionnel)</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
             style={getFocusStyle(name)}
             onFocus={() => setFocused(name)} onBlur={() => setFocused(null)} />
    </div>
  );
}
